import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { CenteredLoader, PlainLink } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import React from 'react'
import styled from 'styled-components'
import { removeCurrentYear } from '@/utils/date'
import { format, formatDistanceToNow } from '@/utils/date-fns'
import { safeParsePhoneNumber } from '@/utils/parsing'
import type { CombinedRowView } from './combined-rows'
import { SeverityContent as BaseSeverity } from './components'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  & > div + div {
    margin-top: 1rem;
  }
`
const Infringement = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'user     status   menu'
    'rule     rule     rule'
    'category category category'
    'date     date     date'
    'vehicle  vehicle  vehicle';
  gap: 6px;

  h3 {
    grid-area: user;
    margin: 0;
  }
  div.menu {
    grid-area: menu;
    margin-left: 1rem;
  }
`
const Severity = styled(BaseSeverity)`
  grid-area: category;
  margin: 0 0 6px -6px;

  div {
    padding: 6px 12px 3px;
    font-size: 0.8rem;
  }
`
const DateField = styled.div`
  grid-area: date;

  div {
    font-style: normal;
    font-weight: 600;
    font-size: 0.9rem;
    line-height: 0.8rem;
  }
  span {
    font-size: 0.7em;
    color: ${props => props.theme.colors.gray6};
  }
`

// Two lines at most; the detail page has the rule in full.
const Rule = styled.div`
  grid-area: rule;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.text.dark};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;

  span {
    font-weight: 600;
  }
`

interface InfringementsCardsProps {
  rows: CombinedRowView[]
  queryLoading: boolean
  /** The row's menu; each infringement type has its own actions. */
  renderMenu(row: CombinedRowView): React.ReactNode
  /** Off for readers who only see their own infringements; the type then
   * heads the card. */
  showUser?: boolean

  onLoadMore: () => void
}

/** The infringement list on phones, one card per row of every type. */
export const InfringementsCards: React.FC<InfringementsCardsProps> = ({
  rows,
  queryLoading,
  renderMenu,
  showUser = true,
}) => {
  const translations = useTranslate({
    sources: {
      DRIVING_REST: 'infringements.source.driving-rest',
      WORKING_TIME: 'infringements.source.working-time',
      TACHOGRAPH_USE: 'infringements.source.tachograph-use',
    },
  })

  return (
    <Wrapper>
      {queryLoading && <CenteredLoader marginTop="1rem" marginBottom="1rem" />}

      {rows.map(row => {
        const date = new Date(row.timestamp)
        const dateString = removeCurrentYear(format(date, 'PPP'))
        const timeAgo = formatDistanceToNow(date, { addSuffix: true })
        const phone = safeParsePhoneNumber(row.user.phoneNumber ?? '')

        return (
          <Infringement key={row.key}>
            <h3>
              <PlainLink
                to={`${row.detailPathBase}/${row.id}`}
                $hoverColor="secondary"
              >
                {showUser
                  ? row.user.fullName
                  : translations.sources[row.source]}
              </PlainLink>

              {showUser && !!row.user.phoneNumber && (
                <a href={`tel:${row.user.phoneNumber}`}>
                  <Icon
                    icon="phone"
                    type="solid"
                    role="link"
                    title={phone}
                    color="secondary"
                    margin="0 0 0 6px"
                  />
                </a>
              )}
            </h3>

            <div className="menu">{renderMenu(row)}</div>

            <Rule>
              {showUser && (
                <>
                  <span>{translations.sources[row.source]}</span>
                  {' · '}
                </>
              )}
              {`${row.identifier}. ${row.text}`}
            </Rule>

            {row.severity && <Severity severity={row.severity} />}

            <DateField>
              <div>{dateString}</div>
              <span>{timeAgo}</span>
            </DateField>
          </Infringement>
        )
      })}
    </Wrapper>
  )
}
